/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import allContracts from '../dataPrefetchContracts';
import appCacheService, {getScopeId} from './CacheService';
import {CacheEntry, CacheScope, CacheTTL} from '@adobe/exc-app/cache';
import DataPrefetchContract, {DataDedupConfig, QueryDefinition} from '@adobe/exc-app/network/DataPrefetchContract';
import Emitter from '@exc/emitter';
import {FetchScope, PrefetchOptions} from '@adobe/exc-app/network';
import {getErrorMessage} from '@exc/shared';
import {gqlQuery} from '@exc/graphql';
import hashString from 'string-hash';
import {mergeValuesByUniqueId, transformVariables} from '../dataPrefetchContracts/dataPrefetchUtils';
import metrics, {Level} from '@adobe/exc-app/metrics';
import {orgHasServiceCode, runInBackground} from '../utils';
import {UserOrgContext} from '../models/Context';

export const SHARED_APP_ID = 'sharedData';

type GqlPrefetchContract<T> = DataPrefetchContract<T> & {gql: QueryDefinition};

const getVersionedKey = (key: string, contract: DataPrefetchContract<any>): string => {
  const hash = hashString(
    contract.gql ? `${contract.gql.query}-${JSON.stringify(contract.gql.variables || {})}` : '');
  return `${hash}/${key}`;
};

export class DataPrefetchService extends Emitter {
  private readonly promises: {[key: string]: Promise<any>} = {};
  private readonly metrics = metrics.create('exc.core.services.DataPrefetchService');
  private readonly contracts: {[key: string]: DataPrefetchContract<any>} = {};

  constructor() {
    super();
    allContracts.forEach(contract => this.contracts[contract.key] = contract);
  }

  /**
   * Executes a graphql query based on a data contract.
   * @template T
   * @param gql - GraphQL Data definition
   * @param context - Core Context
   * @param defaultValue - Default value to return if needed.
   * @param cached - Cached Data (If available)
   * @param contract - Contract to fulfill
   * @returns [T, boolean] - Array containing the query result and default flag
   * (True if default, false if actual data)
   * @private
   */
  private async executeGraphQLQuery<T extends object>(
    gql: QueryDefinition,
    context: UserOrgContext,
    defaultValue: T,
    appId: string,
    cached?: CacheEntry<T>,
    contract?: GqlPrefetchContract<T>
  ): Promise<[T, boolean]> {
    const {dataPath, fetchScope: scope, variables: origVariables, query} = gql;
    const variables = transformVariables(origVariables, context, appId, cached, contract);

    if (scope === FetchScope.SANDBOX && !context.sandbox?.name) {
      // There is no selected sandbox, return default and do not cache.
      return [defaultValue, true];
    }

    let response: T | undefined;
    if (dataPath) {
      const gqlResponse = await gqlQuery<{[root: string]: T}, typeof variables>(query, variables, {scope});
      response = gqlResponse?.[dataPath];
    } else {
      response = await gqlQuery<T, typeof variables>(query, variables, {scope});
    }
    return response ? [response, false] : [defaultValue, true];
  }

  /**
   * Retains original values from the initial, cached query response.
   * @template T
   * @param newValue - New data response
   * @param cachedValue - Cached data response
   * @param dedup - Deduplication config
   * @param keepOriginal - Elements to retain
   * @returns merged value
   * @private
   */
  private mergedWithCachedValue<T extends object>(newValue: T, cachedValue: T, dedup?: DataDedupConfig, keepOriginal?: string[]): T {
    let value = newValue;

    if (dedup) {
      value = mergeValuesByUniqueId(value, cachedValue, dedup);
    }

    if (keepOriginal?.length) {
      keepOriginal.forEach(element => {
        if (element in cachedValue) {
          value = {...value, [element]: (cachedValue as Record<string, any>)[element]};
        }
      });
    }
    return value;
  }

  /**
   * Executes a GraphQL query and cache the result if not default.
   * @template T
   * @param contract - Data contract
   * @param key - Cached item key
   * @param context - Core Context
   * @param appId - Requesting application ID
   * @param cached - Cached Data (If available)
   * @returns Query result + Cache information
   * @private
   */
  private async executeAndCache<T extends object>(
    contract: GqlPrefetchContract<T>,
    key: string,
    context: UserOrgContext,
    appId: string,
    cached?: CacheEntry<T>
  ): Promise<CacheEntry<T>> {
    const {defaultValue, expiry, gql, refreshData, scope} = contract;
    const now = Date.now();
    let query = gql;
    if (cached?.value && refreshData?.gql) {
      // To optimize our query performance, we can use the query needed for refresh
      // and then merge it with the cached data we already have.
      query = refreshData.gql;
    }
    const [queryValue, isDefault] = await this.executeGraphQLQuery(query, context, defaultValue, appId, cached, contract);
    let value = queryValue;
    if (cached?.value && refreshData?.gql && !isDefault) {
      // Use the deduplicate config to merge cached and fresh data.
      // Note currently without dedup, only the fresh data will be used.
      const {dedup, keepOriginal} = refreshData;
      value = this.mergedWithCachedValue(queryValue, cached.value, dedup, keepOriginal);
    }

    this.emit('prefetch', {
      key: contract.key,
      scopeId: getScopeId(scope, context),
      value
    });

    if (expiry !== CacheTTL.DONT_CACHE && !isDefault) {
      try {
        const entry = await appCacheService.set<T>({
          appId,
          context,
          expiry,
          key,
          scope,
          value
        });
        if (entry) {
          return entry;
        }
      } catch (err) {
        const error = getErrorMessage(err);
        this.metrics.event('Prefetch.cacheSetFailed', {error}, {level: Level.ERROR});
      }
    }

    return {
      createdAt: now,
      expiresAt: now,
      key,
      value
    };
  }

  /**
   * Fetches fresh data from the network to fullfil the data contract.
   * @template T
   * @param contract - Data contract
   * @param key - Cached item key
   * @param context - Core Context
   * @param appId - Requesting application ID
   * @param cached - Cached Data (If available)
   * @returns Query result + Cache information
   * @private
   */
  private async getFreshData<T extends object>(
    contract: DataPrefetchContract<T>,
    key: string,
    context: UserOrgContext,
    appId: string,
    cached?: CacheEntry<T>
  ): Promise<CacheEntry<T>> {
    const {defaultValue, fetchWhen} = contract;
    const {imsOrg, imsProfile} = context;
    const now = Date.now();

    if (fetchWhen) {
      // Check if we should prefetch at this point.
      const {serviceCodes} = fetchWhen;
      if (serviceCodes && !orgHasServiceCode(imsOrg, imsProfile, serviceCodes)) {
        // The profile does not have the required service code.
        // Return the default value and do not cache.
        return {
          createdAt: now,
          expiresAt: now,
          key,
          value: defaultValue
        };
      }
    }

    const promiseKey = `gql:query:${key}`;
    let promise: Promise<CacheEntry<T>>|undefined = this.promises[promiseKey];
    if (!promise) {
      if (!contract.gql) {
        // Currently only GraphQL is supported for data prefetching.
        throw new Error('Only GraphQL prefetching currently supported');
      }

      promise = this.executeAndCache(contract as GqlPrefetchContract<T>, key, context, appId, cached);
      promise.finally(() => delete this.promises[promiseKey]);
      this.promises[promiseKey] = promise;
    }

    return promise;
  }

  /**
   * Creates a new Data promise for the given contract.
   * @template T
   * @param {DataPrefetchContract} contract - Data contract
   * @param {UserOrgContext} context - Core Context
   * @param {string} appId - Requesting application ID
   * @param options - Prefetch options
   * @returns {CacheEntry<T>} - Query result + Cache information
   * @private
   */
  private async getDataPromise<T extends object>(
    contract: DataPrefetchContract<T>,
    context: UserOrgContext,
    appId: string,
    options?: PrefetchOptions
  ): Promise<CacheEntry<T>> {
    const {expiry, key, revalidate: {enabled: revalidateEnabled, revalidateAfterSec = 0, immediate} = {}, scope} = contract;
    const versionedKey = getVersionedKey(key, contract);
    const {createdAfter = 0} = options || {};
    let entry: CacheEntry<T> | undefined;
    if (expiry !== CacheTTL.DONT_CACHE) {
      entry = await appCacheService.get<T>({
        appId,
        context,
        key: versionedKey,
        scope
      });
      if (entry && entry.createdAt > createdAfter) {
        // Data is cached. See if we need to fetch still.
        if (revalidateEnabled && (Date.now() - entry.createdAt >= revalidateAfterSec * 1000)) {
          runInBackground(!(immediate || options?.revalidateImmediately)).then(
            () => this.getFreshData(contract, versionedKey, context, appId, entry)
          );
        }
        return entry;
      }
    }
    return this.getFreshData(contract, versionedKey, context, appId, entry);
  }

  /**
   * Checks context data to see if a contract can be fulfilled.
   * @param {string} key - Contract key
   * @param {UserOrgContext} context - Core Context
   * @returns {boolean} False if context has all data needed, true otherwise.
   */
  public isContextMissing(key: string, context: UserOrgContext): boolean {
    const contract = this.contracts[key];
    let contextMissing = !context.imsOrg;
    if (contract) {
      contextMissing = contextMissing ||
        ((contract.scope === CacheScope.SANDBOX || contract.gql?.fetchScope === FetchScope.SANDBOX) && !context.sandbox?.name) ||
        ((contract.scope === CacheScope.SUBORG) && !context.subOrg);
    }
    return contextMissing;
  }

  /**
   * Returns data prefetched for contract.
   * @template T
   * @param {string} key - Contract key
   * @param {UserOrgContext} context - Core Context
   * @param {string} dataAppId - Requesting application ID
   * @param options - Prefetch options
   * @returns {CacheEntry<T>} - Data + Cache information
   */
  public async getData<T>(
    key: string,
    context: UserOrgContext,
    dataAppId = '',
    options?: PrefetchOptions
  ): Promise<CacheEntry<T>> {
    const contract = this.contracts[key];
    if (!contract) {
      throw new Error(`Unknown data contract - ${key}`);
    }
    const appId = (contract.shared || !dataAppId) ? SHARED_APP_ID : dataAppId;
    const promiseKey = `${getScopeId(contract.scope, context)}`;

    let promise: Promise<CacheEntry<T>>|undefined = this.promises[promiseKey];
    if (!promise) {
      promise = this.getDataPromise(contract, context, appId, options);
      promise.catch(err => {
        const error = getErrorMessage(err);
        this.metrics.event('Prefetch.Failed', {error, key}, {level: Level.ERROR});
      }).finally(() => delete this.promises[promiseKey]);
      this.promises[promiseKey] = promise;
    }

    return promise;
  }

  /**
   * Updates data cached for contract.
   * @template T
   * @param {string} key - Contract key
   * @param {UserOrgContext} context - Core Context
   * @param {T} value - Data to be cached
   * @param {string} dataAppId - Requesting application ID
   * @returns {Promise<CacheEntry<any> | undefined>} The new cache entry if successful, otherwise undefined
   */
  public updateCache<T>(
    key: string,
    context: UserOrgContext,
    value: T,
    dataAppId = ''
  ): Promise<CacheEntry<any> | undefined> {
    const contract = this.contracts[key];
    if (!contract) {
      this.metrics.warn(`Prefetch.UpdateFailed - Unknown data contract ${key}`);
      return Promise.resolve(undefined);
    }
    const appId = (contract.shared || !dataAppId) ? SHARED_APP_ID : dataAppId;
    const versionedKey = getVersionedKey(key, contract);
    return appCacheService.set({
      appId,
      context,
      key: versionedKey,
      scope: contract.scope,
      value
    });
  }
}

const dataPrefetchService = new DataPrefetchService();

export default dataPrefetchService;
